import { buildParams } from '../../helpers/path';
import { PaginationRequest, PaginationType } from '../../types/pagination';
import {
  CustomCacheRequest,
  InfiniteScrollRequest,
  LanguageRequest,
  SortRequest,
} from '../../types/props';
import { managerApiUrl } from './manager-api';
import { unwrapManagerData } from './monitoring-api';
import { baseFetch } from './utils.ts/base-fetch';

export type ExternalReviewSourceType = {
  icon: string;
  name: string;
};

export type ExternalResourceListItem = {
  client_uuid: string;
  id: number;
  type: number;
  config?: {
    url?: string;
  };
  repeat_per_day: number;
  provider_url: string;
};

export type ExternalReviewType = {
  id: number; // id записи в нашей системе (id из /aggregation/configs)
  url: string;
  source_url: string;
  source: ExternalReviewSourceType | null;
  collection_frequency: number;
  place_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  type?: number;
  com_id?: number;
  aggregator_config_id?: number; // id конфига в агрегаторе, нужен для удаления
};

export type FetchExternalReviewsRequest = {
  url?: string;
  source_url?: string;
  collection_frequency?: string;
  place_id?: string;
  is_active?: string;
} & SortRequest &
  CustomCacheRequest &
  PaginationRequest &
  InfiniteScrollRequest &
  LanguageRequest;

export const fetchExternalReviews = async (
  params?: FetchExternalReviewsRequest,
): Promise<PaginationType & { external_reviews: ExternalReviewType[] }> => {
  // GET /api/protected/manager/external-resource/list
  let url = `${managerApiUrl}/external-resource/list`;

  const { customCache, ...other } = params || {};
  if (other) {
    url += buildParams(other);
  }

  const responseRaw = await baseFetch(url, {
    customCache,
  });
  const unwrapped = unwrapManagerData<ExternalResourceListItem[]>(responseRaw);
  const list = Array.isArray(unwrapped) ? unwrapped : [];

  const external_reviews: ExternalReviewType[] = list.map((item) => ({
    id: item.id,
    url: item.config?.url || item.provider_url || '',
    source_url: item.provider_url || '',
    source: null,
    collection_frequency: item.repeat_per_day ?? 1,
    place_id: 0,
    is_active: true,
    created_at: '',
    updated_at: '',
    type: item.type,
    com_id: undefined,
    aggregator_config_id: item.id,
  }));

  const pagination: PaginationType = {
    count: external_reviews.length,
    page: 1,
    page_size: external_reviews.length,
    total_pages: 1,
    is_last: true,
    last: 1,
  };

  return {
    ...pagination,
    external_reviews,
  };
};

export const fetchAvailableResources = async (): Promise<{ id: number; name: string; type?: string }[]> => {
  // GET /api/protected/manager/external-resource/aggregation/available
  const response = await baseFetch(`${managerApiUrl}/external-resource/aggregation/available`);
  const data = unwrapManagerData<{ id: number; name: string; type?: string }[]>(response);
  return Array.isArray(data) ? data : [];
};

export type CreateExternalReviewRequest = {
  resource_id: number;
  profile_url: string;
  repeat_per_day: number;
};

export const createExternalReview = async (
  params: CreateExternalReviewRequest,
): Promise<ExternalReviewType> => {
  const response = await baseFetch(`${managerApiUrl}/external-resource/aggregation/add`, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  // API возвращает структуру {status: number, data: any[], error?: string}
  // Проверяем статус в теле ответа (успешный статус обычно 200 или true)
  if (response.status !== undefined) {
    const isSuccess = response.status === 200 || response.status === true || response.status === 1;
    if (!isSuccess) {
      const errorMessage = response.error || response.message || 'Failed to save new client configs';
      throw new Error(errorMessage);
    }
  }

  // Если есть ошибка в ответе, выбрасываем её
  if (response.error) {
    throw new Error(response.error);
  }

  // Возвращаем первый элемент из data, если это массив, иначе весь response
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data[0];
  }
  
  return response.data || response;
};

export type UpdateExternalReviewRequest = Partial<CreateExternalReviewRequest> & {
  config_id: number;
};

export const updateExternalReview = async (
  params: UpdateExternalReviewRequest,
): Promise<ExternalReviewType> => {
  // API не поддерживает обновление напрямую, нужно удалить и создать заново
  const { config_id, ...data } = params;
  
  // Удаляем старую конфигурацию
  await deleteExternalReview(config_id);

  // Создаем новую конфигурацию
  if (data.resource_id && data.profile_url && data.repeat_per_day) {
    return createExternalReview({
      resource_id: data.resource_id,
      profile_url: data.profile_url,
      repeat_per_day: data.repeat_per_day,
    });
  }

  throw new Error('Недостаточно данных для обновления');
};


export const deleteExternalReview = async (aggregator_config_id: number): Promise<void> => {
  const response = await baseFetch(`${managerApiUrl}/external-resource/aggregation/remove`, {
    method: 'POST',
    // backend ожидает поле config_id, но по смыслу это aggregator_config_id
    body: JSON.stringify({ config_id: aggregator_config_id }),
  });

  // API возвращает структуру {status: number, data: any[], error?: string}
  // Проверяем статус в теле ответа
  if (response.status !== undefined) {
    const isSuccess = response.status === 200 || response.status === true || response.status === 1;
    if (!isSuccess) {
      const errorMessage = response.error || response.message || 'Failed to remove config';
      throw new Error(errorMessage);
    }
  }

  // Если есть ошибка в ответе, выбрасываем её
  if (response.error) {
    throw new Error(response.error);
  }
};
